import type { ReactElement, ReactNode } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth, type Capability } from './AuthProvider.js';
import { LoadingState } from '../../components/feedback/index.js';
import { PageHeader } from '../../components/system/index.js';
import { buttonClassName } from '../../components/primitives/index.js';

/**
 * A guard's answer, inside the product rather than instead of it.
 *
 * A guard renders ABOVE the shell in the route tree, so whatever it returns
 * replaces the entire application chrome: rendering a bare `ErrorState` gave a
 * person a panel at the top of an otherwise empty black page, with no topbar,
 * no navigation, and no way anywhere except the browser's Back button. A
 * refusal is still a screen in this product, and it should look like one.
 *
 * It deliberately does NOT reproduce the shell. A seat the reader may not have
 * is not a seat to draw them a sidebar for — the honest answer is the page
 * frame, the statement, and one way back to something they can reach.
 */
function GuardScreen({ title, children }: { title: string; children: ReactNode }): ReactElement {
  return (
    <div className="guard">
      {/*
        A real `<main>` with a real `<h1>`.
        
        This screen replaces the whole shell, so it also replaces the shell's
        landmark and heading — and it had neither. axe reported `landmark-one-
        main`, `page-has-heading-one` and `region` on every refusal: a
        screen-reader user had nothing to jump to and no page heading to orient
        by, on the one screen whose entire job is to explain where they are.
        
        The title is required rather than optional now. An earlier version
        dropped it to avoid repeating "Not available" twice — the duplication
        was real, but the answer was to stop the BODY repeating it, not to
        remove the page's only heading.
      */}
      <main id="main" className="page page--task">
        <PageHeader title={title} />
        {children}
        <p>
          <Link className={buttonClassName('secondary', 'md')} to="/">
            Back to the marketplace
          </Link>
        </p>
      </main>
    </div>
  );
}

/** Unauthenticated → /login, remembering where the user was headed. */
export function RequireAuth(): ReactElement {
  const { account, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <GuardScreen title="One moment">
        <LoadingState rows={4} label="Restoring your session" />
      </GuardScreen>
    );
  }
  if (account === null) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return <Outlet />;
}

/**
 * Capability guard.
 *
 * A signed-in user who lacks the capability is shown the SAME "not available"
 * state a 404 produces, because §4.4 makes 404 and 403 indistinguishable by
 * design — a client that distinguished them would re-open the leak the API
 * closed.
 */
export function RequireCapability({ any }: { any: Capability[] }): ReactElement {
  const { hasAny, loading } = useAuth();
  if (loading) {
    return (
      <GuardScreen title="One moment">
        <LoadingState rows={4} label="Checking access" />
      </GuardScreen>
    );
  }
  if (!hasAny(any)) {
    /*
      §4.4 — a missing capability and a 404 must be indistinguishable, so this
      is the SAME sentence `ErrorState` renders for a not-found. It is written
      out rather than delegated because the heading now belongs to the page,
      and `ErrorState` would have repeated it.
    */
    return (
      <GuardScreen title="Not available">
        <p className="guard__body">
          This isn’t available to you. It may not exist, or it may belong to someone else.
        </p>
      </GuardScreen>
    );
  }
  return <Outlet />;
}
